<template>
  <CustomDrawer
    v-model:modelValue="internalDrawerVisible"
    direction="right"
    width="600px"
    title="Call Details"
    :hide-support-widget="true"
  >
    <SkeletonLoader v-if="fetchingData" :page="loaderPages.CALL_DETAILS" />

    <div
      :id="`${source}_call_details_drawer`"
      class="flex flex-col gap-6 text_color font-medium"
      v-else
    >
      <InfoGrid>
        <div id="contacted" class="flex flex-col gap-2">
          <span class="text-[13px] secondary_color">Contacted</span>

          <div class="flex flex-row items-center gap-2">
            <ProfileIcon :data="callDetails?.to" />
            <span>{{ renderEntity(callDetails?.to) }}</span>
          </div>
        </div>
        <InfoItem label="Called via" :value="renderEntity(calledVia)" itemStyle="gap-2"/>
      </InfoGrid>

      <InfoGrid>
        <InfoItem label="Direction" :value="callDetails?.direction" itemStyle="gap-2"/>
        <InfoItem label="Dispositions">
          <DispositionSelect
            v-model="callDetails.disposition"
            :call-id="id"
            select-class="w-full"
          />
        </InfoItem>
      </InfoGrid>

      <InfoGrid>
        <InfoItem label="Date" :value="callDetails?.date" itemStyle="gap-2"/>
        <InfoItem label="Time" :value="callDetails?.time" itemStyle="gap-2"/>
      </InfoGrid>


      <div class="flex items-center gap-2">
        <input
          type="checkbox"
          id="add-to-dnc"
          v-model="addToDNCList"
          @change="handleDNCToggle"
          class="size-3"
        />
        <label for="add-to-dnc" class="text-sm font-semibold secondary_color">
          Add to Do Not Call List
        </label>
      </div>
    
      <div
        class="flex flex-col secondary_color font-normal text-sm mail_reply_container"
      >
        <QuillEditor
          v-model="callDetails.note"
          class="w-full text_color"
          ref="quillEditorRef"
          :height="200"
          @update:model-value="updateNote"
          auto-focus
        />
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end">
        <a-button
          class="primary_medium"
          @click="toHandleAddCallNote"
          :loading="updatingNotes"
          >Log Activity</a-button
        >
      </div>
    </template>
  </CustomDrawer>
</template>

<script setup>
import { ref, watch, computed, onMounted, onUnmounted } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { formatDateTime, renderEntity } from "@/src/utils/utils.js";
import {
  toAddCallNote,
  toGetActivityWithSSID,
  addToDNDBlocklist,
  removeFromDNCBlocklist,
  getDisplayStatus,
  getCallSetting,
} from "@/src/utils/callsUtils.js";
import { message } from "ant-design-vue";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import { dndStatus, callSettingKeys } from "@/src/constants/callConstants.js";
import eventBus from "@/src/utils/eventBus.js";
import CallService from "@/src/api/CallService.js";
import CustomDrawer from "@/src/components/CustomDrawer.vue";
import ProfileIcon from "@/src/components/ProfileIcon.vue";
import InfoGrid from "@/src/components/info_components/InfoGrid.vue";
import InfoItem from "@/src/components/info_components/InfoItem.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import QuillEditor from "@/src/components/quill_editor/QuillEditor.vue";
import DispositionSelect from "@/src/components/call_components/DispositionSelect.vue";

const store = useStore();
const router = useRouter();

const props = defineProps({
  id: { type: String, required: true },
  source: { type: String, required: true },
  drawerVisible: { type: Boolean, required: true },
});

const emit = defineEmits(["update:drawerVisible"]);

const quillEditorRef = ref(null);
const fetchingData = ref(false);
const updatingNotes = ref(false);
const logActivityClicked = ref(false);
const addToDNCList = ref(false);
const isDispositionMandatory = ref(false);

const getPhoneNumberFromCall = () => {
  if (callDetails.value.direction === "Outgoing") {
    return callDetails.value.to_number;
  } else {
    return callDetails.value.from_number;
  }
};

const isPhoneNumberInDNC = (response, phoneNumber) => {
  if (!response?.data?.DND_Status || !Array.isArray(response.data.DND_Status)) {
    return false;
  }
  const dndEntry = response.data.DND_Status.find(
    entry => entry.phone_number === phoneNumber
  );
  
  return dndEntry?.dnd_status === dndStatus.DO_NOT_CALL;
};

const checkPhoneNumberDNDStatus = async () => {
  try {
    const phoneNumber = getPhoneNumberFromCall();
    
    if (!phoneNumber) {
      addToDNCList.value = false;
      return;
    }

    const response = await CallService.getDNDList({ 
      page: 1, 
      page_size: 20,
      search_query: phoneNumber
    });
    
    addToDNCList.value = isPhoneNumberInDNC(response, phoneNumber);
  } catch (error) {
    console.error("Error checking phone number DND status:", error);
    addToDNCList.value = false;
  }
};

const fetchDispositionMandatorySetting = async () => {
  try {
    const value = await getCallSetting(callSettingKeys.DISPOSITION_MANDATORY);
    isDispositionMandatory.value = value;
  } catch (error) {
    console.error("Error fetching disposition mandatory setting:", error);
    isDispositionMandatory.value = false;
  }
};

const fetchCallDetailsSettings = async () => {
  try {
    await Promise.all([
      checkPhoneNumberDNDStatus(),
      fetchDispositionMandatorySetting()
    ]);
  } catch (error) {
    console.error("Error fetching call details settings:", error);
  }
};

const internalDrawerVisible = ref(props.drawerVisible);
const callDetails = ref({
  called_via: "",
  status: "",
  hangup_reason: "",
  disposition: "",
  direction: "",
  date: "",
  time: "",
  duration: "",
  note: "",
  to: null,
  from: null,
  to_number: null,
  from_number: null,
  entity_id: null,
  entity_type: null,
});

const currentNote = computed(() => store.getters["calls/currentCallNote"]);

const calledVia = computed(() => 
  callDetails.value?.called_via?.phone_number || 
  callDetails.value?.called_via || 
  callDetails.value?.from_number || 
  null
);

const displayStatus = computed(() => getDisplayStatus(callDetails.value));

const updateNote = (value) => {
  callDetails.value.note = value;
  store.dispatch("calls/setCallNote", value);
};

const resetValues = () => {
  callDetails.value.note = "";
  store.dispatch("calls/setCallNote", "");
};

const handleGetActivityWithSSID = async () => {
  try {
    logActivityClicked.value = false;
    
    let data = await toGetActivityWithSSID(props.id);

    if (data.call_status === "In-progress") {
      setTimeout(async () => {
        data = await toGetActivityWithSSID(props.id);
      }, 1000);
    }
    callDetails.value.to = data.to;
    callDetails.value.called_via = data.from;
    callDetails.value.status = data.call_status;
    callDetails.value.hangup_reason = data.hangup_reason || "";
    callDetails.value.disposition = data.call_disposition;
    callDetails.value.direction = data.call_direction;
    callDetails.value.date = formatDateTime(data.created_at, "date");
    callDetails.value.time = formatDateTime(data.created_at, "time");
    callDetails.value.duration = data.duration + "s";
    callDetails.value.note = currentNote.value;
    callDetails.value.to_number = data.to_number;
    callDetails.value.from_number = data.from_number;
    callDetails.value.entity_id = data.entity_id;
    callDetails.value.entity_type = data.entity_type;

    await fetchCallDetailsSettings();

  } catch (error) {
    console.error("An occurred while trying to get activity details: ", error);
  } finally {
    fetchingData.value = false;
  }
};

const validateDisposition = () => {
  if (isDispositionMandatory.value && !callDetails.value.disposition) {
    message.error("Please select a disposition");
    return false;
  }
  return true;
};

const toHandleAddCallNote = async () => {
  if (!validateDisposition()) {
    return;
  }

  try {
    updatingNotes.value = true;

    if (callDetails.value.note) {
      const response = await toAddCallNote(props.id, callDetails.value.note);
      if (response) {
        logActivityClicked.value = true;
        message.success("Call Activity successfully added");
      }
    }
  } catch (error) {
    console.error("An error occurred while adding call note: ", error);
  } finally {
    emit("update:drawerVisible", false);
    eventBus.emit("call-log-added", true);
    updatingNotes.value = false;
    if (store.getters["callsCampaign/isDrawerVisible"]) {
      store.dispatch("callsCampaign/setDrawerVisible", false);
      eventBus.emit("campaign-drawer-off", true);
    }
  }
};

const handleDNCToggle = async () => {
  const phoneNumber = getPhoneNumberFromCall();
  
  if (!phoneNumber) {
    message.error("Phone number not found");
    addToDNCList.value = false;
    return;
  }

  const isAdding = addToDNCList.value;
  
  try {
    const result = isAdding 
      ? await addToDNDBlocklist(phoneNumber, getEntityFromCallDetails())
      : await removeFromDNCBlocklist(phoneNumber);
    
    if (result.success) {
      message.success(`${isAdding ? 'Added to' : 'Removed from'} Do Not Call List successfully!`);
    } else {
      message.error(`Failed to ${isAdding ? 'add' : 'remove'} from Do Not Call List`);
      addToDNCList.value = !isAdding;
    }
  } catch (error) {
    console.error(`Error ${isAdding ? 'adding to' : 'removing from'} DNC:`, error);
    message.error(`Failed to ${isAdding ? 'add' : 'remove'} from Do Not Call List`);
    addToDNCList.value = !isAdding;
  }
};

const getEntityFromCallDetails = () => {
  const { entity_id, entity_type } = callDetails.value;
  return entity_id && entity_type && entity_id !== 0 
    ? { id: entity_id, type: entity_type } 
    : null;
};

watch(
  () => props.id,
  (newVal) => {
    if (newVal) {
      fetchingData.value = true;
      handleGetActivityWithSSID();
    }
  },
  { immediate: true }
);

watch(
  () => props.drawerVisible,
  (newVal) => {
    if (!newVal && !validateDisposition()) {
      emit("update:drawerVisible", true);
      return;
    }

    internalDrawerVisible.value = newVal;
    if (!newVal) eventBus.emit("call-log-added", true);
  }
);

watch(
  () => internalDrawerVisible.value,
  async (newVal) => {
    if (!newVal) {
      if (!validateDisposition()) {
        internalDrawerVisible.value = true;
        return;
      }
      if (!logActivityClicked.value) {
        await toHandleAddCallNote();
      }
      resetValues();
      eventBus.emit("activity-logged", props.id);
      // Notify backend that call log drawer was closed so workflow events can be published
      // Must run after toHandleAddCallNote completes so disposition/notes are saved first
      CallService.notifyCallLogClosed(props.id);
    }
  }
);

watch(currentNote, (newNote) => {
  callDetails.value.note = newNote;
});
</script>